import type { SocialProofAvatar } from "@/features/avatars/social-proof-avatar-deck";
import { pickSignalCardAvatars } from "@/lib/social-proof/pickAvatars";

export type AvatarPreloadMetrics = {
  cacheHits: number;
  decoded: number;
  failed: number;
  requested: number;
  revealWaitMs: number;
  selectedDeck: string[];
};

declare global {
  interface Window {
    __QB_AVATAR_PRELOAD_METRICS__?: AvatarPreloadMetrics;
  }
}

const ROTATION_SEED_STEP = 7_919;
const LOAD_EVENT_TIMEOUT_MS = 700;
const PRELOAD_SEEDS = buildSignalAvatarPreloadSeeds();
const decodedAvatarSrcs = new Set<string>();
const failedAvatarSrcs = new Set<string>();
const pendingAvatarSrcs = new Map<string, Promise<boolean>>();
const requestedAvatarSrcs = new Set<string>();

export function preloadSocialProofAvatars(seed?: number): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  const sources =
    seed === undefined ? signalAvatarPreloadSources() : deckSrcs(seed);
  return preloadAvatarSources(sources).then(() => undefined);
}

export function preloadSignalAvatarDeck(
  seed: number,
): Promise<readonly SocialProofAvatar[]> {
  if (typeof window === "undefined")
    return Promise.resolve(pickSignalCardAvatars(seed));
  const startedAt = performance.now();
  const deck = pickSignalCardAvatars(seed);
  return preloadAvatarSources(deck.map((avatar) => avatar.src)).then(() => {
    updateRevealWait(startedAt);
    updateSelectedDeck(deck);
    return deck;
  });
}

export function signalAvatarPreloadSources(): readonly string[] {
  return Array.from(new Set(PRELOAD_SEEDS.flatMap(deckSrcs)));
}

export function isSignalAvatarDeckDecoded(seed: number): boolean {
  return deckSrcs(seed).every((src) => decodedAvatarSrcs.has(src));
}

export function readySignalAvatarSeed(preferredSeed: number): number {
  if (isSignalAvatarDeckDecoded(preferredSeed)) return preferredSeed;
  return PRELOAD_SEEDS.find(isSignalAvatarDeckDecoded) ?? PRELOAD_SEEDS[0]!;
}

export function resetAvatarPreloadForTests(): void {
  decodedAvatarSrcs.clear();
  failedAvatarSrcs.clear();
  pendingAvatarSrcs.clear();
  requestedAvatarSrcs.clear();
  if (typeof window !== "undefined") {
    window.__QB_AVATAR_PRELOAD_METRICS__ = initialMetrics();
  }
}

function preloadAvatarSources(sources: readonly string[]): Promise<boolean[]> {
  return Promise.all(sources.map(preloadAvatarSource));
}

function preloadAvatarSource(src: string): Promise<boolean> {
  const metrics = ensureMetrics();
  if (decodedAvatarSrcs.has(src)) {
    metrics.cacheHits += 1;
    return Promise.resolve(true);
  }
  const pending = pendingAvatarSrcs.get(src);
  if (pending) {
    metrics.cacheHits += 1;
    return pending;
  }
  requestedAvatarSrcs.add(src);
  metrics.requested = requestedAvatarSrcs.size;
  const promise = decodeAvatarSource(src).then((ok) => trackDecoded(src, ok));
  pendingAvatarSrcs.set(src, promise);
  return promise;
}

function decodeAvatarSource(src: string): Promise<boolean> {
  const image = new window.Image();
  image.decoding = "sync";
  image.src = src;
  if (typeof image.decode === "function") {
    return image.decode().then(
      () => true,
      () => false,
    );
  }
  return decodeByLoadEvents(image);
}

function decodeByLoadEvents(image: HTMLImageElement): Promise<boolean> {
  if (image.complete && image.naturalWidth > 0) return Promise.resolve(true);
  return new Promise((resolve) => {
    const timeout = window.setTimeout(
      () => resolve(false),
      LOAD_EVENT_TIMEOUT_MS,
    );
    image.onload = () => {
      window.clearTimeout(timeout);
      resolve(true);
    };
    image.onerror = () => {
      window.clearTimeout(timeout);
      resolve(false);
    };
  });
}

function trackDecoded(src: string, ok: boolean): boolean {
  pendingAvatarSrcs.delete(src);
  const metrics = ensureMetrics();
  if (ok) {
    decodedAvatarSrcs.add(src);
    metrics.decoded = decodedAvatarSrcs.size;
    return true;
  }
  failedAvatarSrcs.add(src);
  metrics.failed = failedAvatarSrcs.size;
  return false;
}

function deckSrcs(seed: number): readonly string[] {
  return pickSignalCardAvatars(seed).map((avatar) => avatar.src);
}

function buildSignalAvatarPreloadSeeds(): readonly number[] {
  const bases = [362, 421, 511, 683];
  return bases.flatMap((seed) => rotationSeeds(seed, 2));
}

function rotationSeeds(seed: number, count: number): readonly number[] {
  return Array.from(
    { length: count },
    (_, index) => seed + index * ROTATION_SEED_STEP,
  );
}

function ensureMetrics(): AvatarPreloadMetrics {
  if (typeof window === "undefined" || !shouldExposeMetrics()) {
    return hiddenMetrics;
  }
  window.__QB_AVATAR_PRELOAD_METRICS__ ??= initialMetrics();
  return window.__QB_AVATAR_PRELOAD_METRICS__;
}

function updateRevealWait(startedAt: number): void {
  const metrics = ensureMetrics();
  metrics.revealWaitMs = Math.max(
    metrics.revealWaitMs,
    performance.now() - startedAt,
  );
}

function updateSelectedDeck(deck: readonly SocialProofAvatar[]): void {
  ensureMetrics().selectedDeck = deck.map((avatar) => avatar.src);
}

function initialMetrics(): AvatarPreloadMetrics {
  return {
    cacheHits: 0,
    decoded: 0,
    failed: 0,
    requested: 0,
    revealWaitMs: 0,
    selectedDeck: [],
  };
}

function shouldExposeMetrics(): boolean {
  return import.meta.env.DEV || import.meta.env.MODE === "test";
}

const hiddenMetrics: AvatarPreloadMetrics = initialMetrics();
